import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { map } from 'rxjs';
import {
  ImportCount,
  ImportElementType,
  ImportMessageGroup,
  ImportMessageLevel,
  ImportReport,
  ImportReportMessage,
} from '../../../models/classic-import.model';
import {
  SegmentedFilterControlComponent,
  SegmentedFilterItem,
} from '../../../components/segmented-filter-control/segmented-filter-control.component';

const TILE_TYPES: readonly ImportElementType[] = ['TEST_CASE', 'TEST_MODULE', 'DATA_SHEET', 'OBJECT_LIBRARY'];
const LEVELS: readonly ImportMessageLevel[] = ['ERROR', 'WARNING', 'INFO'];
const FILTER_GROUPS: readonly ImportMessageGroup[] = ['REBUILT', 'NOT_EXECUTABLE', 'LOST', 'NOTE'];
const GROUP_ORDER: readonly ImportMessageGroup[] = ['BLOCKING', ...FILTER_GROUPS];
const ZERO: ImportCount = { created: 0, replaced: 0, unchanged: 0, skipped: 0 };
/** Ab so vielen Meldungen starten die Gruppen zugeklappt, damit der Bericht lesbar bleibt. */
const COLLAPSE_ABOVE = 50;

interface MessageGroup {
  group: ImportMessageGroup;
  messages: ImportReportMessage[];
}

/** Probelauf-Bericht eines Classic-Imports: Sperrgründe, Zahlen, Anteile je Repository und filterbare Meldungen. */
@Component({
  selector: 'app-classic-import-report',
  imports: [TranslatePipe, MatIcon, SegmentedFilterControlComponent],
  templateUrl: './classic-import-report.component.html',
  styleUrl: './classic-import-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassicImportReportComponent {
  private readonly translate = inject(TranslateService);

  readonly report = input.required<ImportReport>();
  /** Nur den Sperrkasten zeigen, etwa im Schritt „Auswahl“. */
  readonly blockersOnly = input(false);
  readonly download = output<void>();

  readonly level = signal<ImportMessageLevel | null>(null);
  readonly group = signal<ImportMessageGroup | null>(null);

  private readonly lang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly tiles = computed(() => TILE_TYPES.map((type) => ({ type, count: this.report().counts[type] ?? ZERO })));

  readonly levelItems = computed<SegmentedFilterItem[]>(() => {
    this.lang();
    return this.filterItems(LEVELS, 'level', 'levels');
  });

  readonly groupItems = computed<SegmentedFilterItem[]>(() => {
    this.lang();
    return this.filterItems(FILTER_GROUPS, 'group', 'groups');
  });

  readonly groups = computed<MessageGroup[]>(() => {
    const level = this.level();
    const group = this.group();
    const matching = this.report().messages.filter(
      (m) => (level === null || m.level === level) && (group === null || m.group === group),
    );
    return GROUP_ORDER.map((g) => ({ group: g, messages: matching.filter((m) => m.group === g) })).filter(
      (g) => g.messages.length > 0,
    );
  });

  private readonly expanded = linkedSignal<ImportReport, Partial<Record<ImportMessageGroup, boolean>>>({
    source: this.report,
    computation: () => ({}),
  });

  private readonly collapsedByDefault = computed(() => this.report().messages.length > COLLAPSE_ABOVE);

  isExpanded(group: ImportMessageGroup): boolean {
    return this.expanded()[group] ?? !this.collapsedByDefault();
  }

  toggle(group: ImportMessageGroup): void {
    const open = this.isExpanded(group);
    this.expanded.update((state) => ({ ...state, [group]: !open }));
  }

  // Wie ImportReportMarkdownWriter im BE: `origin.line` ist die Classic-Schrittnummer ab 1. `message.step` zählt
  // die NG-Schritte und weicht davon ab, sobald ein Classic-Schritt geteilt wurde.
  origin(message: ImportReportMessage): string {
    const origin = message.origin;
    if (!origin?.file) return '–';
    if (origin.line === null) return origin.file;
    return `${origin.file}, ${this.translate.instant('classicImport.report.step', { n: origin.line })}`;
  }

  onLevel(id: string | null): void {
    this.level.set(id as ImportMessageLevel | null);
  }

  onGroup(id: string | null): void {
    this.group.set(id as ImportMessageGroup | null);
  }

  private filterItems(ids: readonly string[], prefix: string, labels: string): SegmentedFilterItem[] {
    return [
      { id: null, label: this.translate.instant('classicImport.report.all'), testId: `ci-filter-${prefix}-all` },
      ...ids.map((id) => ({
        id,
        label: this.translate.instant(`classicImport.report.${labels}.${id}`),
        testId: `ci-filter-${prefix}-${id}`,
      })),
    ];
  }
}
